/**
 * Small IR shape helpers shared by lowering and lifting (W1-04 core-ir): mutable views of IR records, control
 * points in both directions, the JSON form of an event (for lists kept verbatim in `IrExtended.ignoredLists`), and
 * the RPE list keys of the line properties.
 */

import { PROP_SPECS } from '../model/props';
import type { ControlCurve, ControlPoint, ExtProp, LayeredProp, LineProp } from '../model/types';
import { setOwn } from '../util/json';
import type { Json } from '../util/types';
import type { IrControlPoint, IrEvent, IrLine } from './types';

export type MutableIrLine = { -readonly [K in keyof IrLine]: IrLine[K] } & {
  eventLayers: IrLine['eventLayers'][number][];
};

/** RPE list key of a line property ('alphaEvents', 'scaleXEvents', ...). */
export function listKeyOf(prop: LineProp): string {
  return PROP_SPECS[prop].rpeKey;
}

/** eventLayers list keys in Phira's scan order. */
export const LAYER_PROPS_IN_ORDER: readonly LayeredProp[] = ['alpha', 'moveX', 'moveY', 'rotate', 'speed'];
/** extended list keys in a stable order (the IR is keyed, order does not reach the JSON). */
export const EXT_PROPS_IN_ORDER: readonly ExtProp[] = ['scaleX', 'scaleY', 'color', 'text', 'incline', 'paint', 'gif'];

/** Model control curve -> IR control points. */
export function controlOut(curve: ControlCurve): IrControlPoint[] {
  return curve.points.map((p) => ({ x: p.x, easing: p.easing, value: p.value, extra: p.extra ?? [] }));
}

/** IR control points -> model control curve. */
export function controlIn(points: readonly IrControlPoint[]): ControlCurve {
  return {
    points: points.map((p): ControlPoint => {
      const base = { x: p.x, easing: p.easing, value: p.value };
      return p.extra.length > 0 ? { ...base, extra: p.extra } : base;
    }),
  };
}

/** The JSON object of an event as RPE writes it (known keys, then unknown keys that do not collide). */
export function eventJson(e: IrEvent<number | readonly number[] | string>): Json {
  const o: Record<string, Json> = {
    startTime: [...e.startTime],
    endTime: [...e.endTime],
    start: typeof e.start === 'object' ? [...e.start] : e.start,
    end: typeof e.end === 'object' ? [...e.end] : e.end,
    easingType: e.easingType,
    easingLeft: e.easingLeft,
    easingRight: e.easingRight,
    bezier: e.bezier,
    bezierPoints: [...e.bezierPoints],
    linkgroup: e.linkgroup,
  };
  for (const [k, v] of e.extra) if (!Object.prototype.hasOwnProperty.call(o, k)) setOwn(o, k, v);
  return o;
}
